import React, { useState, useEffect } from 'react';
import { designerAPI, scoreAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Save, 
  Calendar,
  CheckCircle,
  Info,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  AlertCircle,
  Lightbulb,
  Target,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { MONTHS, PERFORMANCE_PARAMS, ERROR_CONTROL_PARAM, getCurrentWeek } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import { cn } from '../lib/utils';

// Score button component with tooltip
const ScoreButton = ({ value, selected, onClick, label, description, isDark }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "relative flex-1 h-12 rounded-lg border-2 font-bold text-lg transition-all group",
      selected
        ? value <= 2 
          ? "bg-red-500 border-red-500 text-white"
          : value === 3
            ? "bg-amber-500 border-amber-500 text-white"
            : "bg-emerald-500 border-emerald-500 text-white"
        : isDark
          ? "border-slate-600 text-slate-300 hover:border-slate-500"
          : "border-slate-200 text-slate-600 hover:border-slate-400"
    )}
  >
    {value}
    {/* Tooltip on hover */}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
      {label}
    </div>
  </button>
);

// Parameter card with expandable details
const ParameterCard = ({ param, value, reason, onScoreChange, onReasonChange, isDark, expanded, onToggle }) => {
  const selectedDescription = value > 0 ? param.descriptions[value - 1] : null;
  const selectedReasonExample = value > 0 ? param.reasonExamples[value - 1] : null;

  return (
    <Card className={cn(
      "overflow-hidden transition-all",
      isDark ? "bg-slate-800 border-slate-700" : "",
      expanded && "ring-2 ring-[#331062]"
    )}>
      <CardHeader 
        className={cn(
          "cursor-pointer select-none",
          isDark ? "hover:bg-slate-700/50" : "hover:bg-slate-50"
        )}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className={cn("text-base font-semibold", isDark ? "text-white" : "")}>
              {param.label}
            </CardTitle>
            {value > 0 && (
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs font-medium",
                value <= 2 ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300" :
                value === 3 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" :
                "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
              )}>
                {value}/5 - {param.scale[value - 1]}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
              {param.tooltip}
            </span>
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </CardHeader>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="pt-0 space-y-4">
              {/* Why It Matters */}
              <div className={cn(
                "p-3 rounded-lg text-sm",
                isDark ? "bg-slate-700/50" : "bg-blue-50"
              )}>
                <p className={cn("flex items-start gap-2", isDark ? "text-slate-300" : "text-blue-700")}>
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Why this matters:</strong> {param.whyItMatters}</span>
                </p>
              </div>

              {/* Score Buttons */}
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Select Score (1-5)</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((score) => (
                    <ScoreButton
                      key={score}
                      value={score}
                      selected={value === score}
                      onClick={() => onScoreChange(score)}
                      label={param.scale[score - 1]}
                      description={param.descriptions[score - 1]}
                      isDark={isDark}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-xs text-slate-400 px-1">
                  <span>1 - {param.scale[0]}</span>
                  <span>5 - {param.scale[4]}</span>
                </div>
              </div>

              {/* Selected Score Description */}
              {selectedDescription && (
                <div className={cn(
                  "p-3 rounded-lg",
                  isDark ? "bg-slate-700" : "bg-slate-50"
                )}>
                  <p className={cn("text-sm font-medium mb-1", isDark ? "text-white" : "text-slate-700")}>
                    Score {value} means:
                  </p>
                  <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                    {selectedDescription}
                  </p>
                </div>
              )}

              {/* Reason for Score */}
              <div className="space-y-2">
                <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                  <MessageSquare className="w-4 h-4" />
                  Reason for Score
                  <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={reason || ''}
                  onChange={(e) => onReasonChange(e.target.value)}
                  placeholder={selectedReasonExample ? `Example: ${selectedReasonExample}` : "Provide a brief explanation for this score..."}
                  rows={2}
                  className={cn(isDark ? "bg-slate-700 border-slate-600" : "")}
                />
                {selectedReasonExample && (
                  <p className={cn("text-xs italic", isDark ? "text-slate-500" : "text-slate-400")}>
                    Example: {selectedReasonExample}
                  </p>
                )}
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

const WeeklyScores = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const currentDate = new Date();
  const [designers, setDesigners] = useState([]);
  const [selectedDesigner, setSelectedDesigner] = useState('');
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [existingScores, setExistingScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedParams, setExpandedParams] = useState({});
  
  const [formData, setFormData] = useState({
    quality_consistency: 0,
    brief_understanding: 0,
    speed_vs_accuracy: 0,
    guideline_adherence: 0,
    team_discipline: 0,
    error_control: 0,
    // Reasons for each score
    quality_consistency_reason: '',
    brief_understanding_reason: '',
    speed_vs_accuracy_reason: '',
    guideline_adherence_reason: '',
    team_discipline_reason: '',
    error_control_reason: '',
    // Active days and contribution (new policy fields)
    active_days: 5,
    contribution_score: 100,
    // Additional fields
    escalation: 'None',
    escalation_reason: '',
    escalation_screenshot: null,
    positive_feedback: false,
    positive_feedback_type: 'Normal',
    positive_feedback_description: '',
    positive_feedback_screenshot: null,
    unplanned_leave: false,
    // Manager Comment Section
    strength_observed: '',
    area_for_improvement: '',
    reason_for_overall_score: '',
    expected_improvement: '',
  });

  useEffect(() => {
    const fetchDesigners = async () => {
      try {
        const response = await designerAPI.getAll();
        setDesigners(response.data);
      } catch (error) {
        console.error('Error fetching designers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDesigners();
  }, []);

  useEffect(() => {
    if (selectedDesigner && selectedYear && selectedMonth) {
      fetchExistingScores();
    }
  }, [selectedDesigner, selectedYear, selectedMonth]);

  const fetchExistingScores = async () => {
    try {
      const response = await scoreAPI.getAll({
        designer_id: selectedDesigner,
        year: selectedYear,
        month: selectedMonth,
      });
      setExistingScores(response.data);
      
      const existingWeekScore = response.data.find(s => s.week === selectedWeek);
      if (existingWeekScore) {
        setFormData({
          quality_consistency: existingWeekScore.quality_consistency || 0,
          brief_understanding: existingWeekScore.brief_understanding || 0,
          speed_vs_accuracy: existingWeekScore.speed_vs_accuracy || 0,
          guideline_adherence: existingWeekScore.guideline_adherence || 0,
          team_discipline: existingWeekScore.team_discipline || 0,
          error_control: existingWeekScore.error_control || 0,
          quality_consistency_reason: existingWeekScore.quality_consistency_reason || '',
          brief_understanding_reason: existingWeekScore.brief_understanding_reason || '',
          speed_vs_accuracy_reason: existingWeekScore.speed_vs_accuracy_reason || '',
          guideline_adherence_reason: existingWeekScore.guideline_adherence_reason || '',
          team_discipline_reason: existingWeekScore.team_discipline_reason || '',
          error_control_reason: existingWeekScore.error_control_reason || '',
          active_days: existingWeekScore.active_days ?? 5,
          contribution_score: existingWeekScore.contribution_score ?? 100,
          escalation: existingWeekScore.escalation || 'None',
          escalation_reason: existingWeekScore.escalation_reason || '',
          escalation_screenshot: existingWeekScore.escalation_screenshot || null,
          positive_feedback: existingWeekScore.positive_feedback || false,
          positive_feedback_type: existingWeekScore.positive_feedback_type || 'Normal',
          positive_feedback_description: existingWeekScore.positive_feedback_description || '',
          positive_feedback_screenshot: existingWeekScore.positive_feedback_screenshot || null,
          unplanned_leave: existingWeekScore.unplanned_leave || false,
          strength_observed: existingWeekScore.strength_observed || '',
          area_for_improvement: existingWeekScore.area_for_improvement || '',
          reason_for_overall_score: existingWeekScore.reason_for_overall_score || '',
          expected_improvement: existingWeekScore.expected_improvement || '',
        });
      } else {
        resetForm();
      }
    } catch (error) {
      console.error('Error fetching scores:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      quality_consistency: 0,
      brief_understanding: 0,
      speed_vs_accuracy: 0,
      guideline_adherence: 0,
      team_discipline: 0,
      error_control: 0,
      quality_consistency_reason: '',
      brief_understanding_reason: '',
      speed_vs_accuracy_reason: '',
      guideline_adherence_reason: '',
      team_discipline_reason: '',
      error_control_reason: '',
      active_days: 5,
      contribution_score: 100,
      escalation: 'None',
      escalation_reason: '',
      escalation_screenshot: null,
      positive_feedback: false,
      positive_feedback_type: 'Normal',
      positive_feedback_description: '',
      positive_feedback_screenshot: null,
      unplanned_leave: false,
      strength_observed: '',
      area_for_improvement: '',
      reason_for_overall_score: '',
      expected_improvement: '',
    });
    setExpandedParams({});
  };

  const handleWeekChange = (week) => {
    setSelectedWeek(week);
    const existingWeekScore = existingScores.find(s => s.week === week);
    if (existingWeekScore) {
      setFormData({
        quality_consistency: existingWeekScore.quality_consistency || 0,
        brief_understanding: existingWeekScore.brief_understanding || 0,
        speed_vs_accuracy: existingWeekScore.speed_vs_accuracy || 0,
        guideline_adherence: existingWeekScore.guideline_adherence || 0,
        team_discipline: existingWeekScore.team_discipline || 0,
        error_control: existingWeekScore.error_control || 0,
        quality_consistency_reason: existingWeekScore.quality_consistency_reason || '',
        brief_understanding_reason: existingWeekScore.brief_understanding_reason || '',
        speed_vs_accuracy_reason: existingWeekScore.speed_vs_accuracy_reason || '',
        guideline_adherence_reason: existingWeekScore.guideline_adherence_reason || '',
        team_discipline_reason: existingWeekScore.team_discipline_reason || '',
        error_control_reason: existingWeekScore.error_control_reason || '',
        active_days: existingWeekScore.active_days ?? 5,
        contribution_score: existingWeekScore.contribution_score ?? 100,
        escalation: existingWeekScore.escalation || 'None',
        escalation_reason: existingWeekScore.escalation_reason || '',
        escalation_screenshot: existingWeekScore.escalation_screenshot || null,
        positive_feedback: existingWeekScore.positive_feedback || false,
        positive_feedback_type: existingWeekScore.positive_feedback_type || 'Normal',
        positive_feedback_description: existingWeekScore.positive_feedback_description || '',
        positive_feedback_screenshot: existingWeekScore.positive_feedback_screenshot || null,
        unplanned_leave: existingWeekScore.unplanned_leave || false,
        strength_observed: existingWeekScore.strength_observed || '',
        area_for_improvement: existingWeekScore.area_for_improvement || '',
        reason_for_overall_score: existingWeekScore.reason_for_overall_score || '',
        expected_improvement: existingWeekScore.expected_improvement || '',
      });
    } else {
      resetForm();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDesigner) {
      toast.error('Please select a designer');
      return;
    }
    
    // Validate all 6 parameters have scores (5 core + error_control per new policy)
    const allParams = [...PERFORMANCE_PARAMS.map(p => p.key), 'error_control'];
    const hasAllScores = allParams.every(key => formData[key] > 0);
    if (!hasAllScores) {
      toast.error('Please provide scores for all 6 parameters');
      return;
    }

    // Validate reasons are provided for all scores
    const missingReasons = allParams.filter(key => formData[key] > 0 && !formData[`${key}_reason`]);
    if (missingReasons.length > 0) {
      toast.error('Please provide reasons for all scores');
      return;
    }

    // Validate escalation reason if escalation is set
    if (formData.escalation !== 'None' && !formData.escalation_reason) {
      toast.error('Please provide an escalation reason');
      return;
    }

    // Validate active days
    if (formData.active_days < 1 || formData.active_days > 5) {
      toast.error('Active days must be between 1 and 5');
      return;
    }

    setSaving(true);
    try {
      const scoreData = {
        ...formData,
        designer_id: selectedDesigner,
        year: selectedYear,
        month: selectedMonth,
        week: selectedWeek,
      };

      const existingScore = existingScores.find(s => s.week === selectedWeek);
      
      if (existingScore) {
        await scoreAPI.update(existingScore.id, scoreData);
        toast.success('Score updated successfully');
      } else {
        await scoreAPI.create(scoreData);
        toast.success('Score saved successfully');
      }
      
      fetchExistingScores();
    } catch (error) {
      console.error('Error saving score:', error);
      toast.error(error.response?.data?.detail || 'Failed to save score');
    } finally {
      setSaving(false);
    }
  };

  const weekHasScore = (week) => existingScores.some(s => s.week === week);
  
  const toggleParam = (key) => {
    setExpandedParams(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedDesignerData = designers.find(d => d.id === selectedDesigner);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="weekly-scores-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div>
        <h1 className={cn("text-2xl md:text-3xl font-bold font-outfit", isDark ? "text-white" : "text-slate-800")}>
          Weekly Score Entry
        </h1>
        <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
          Record performance scores for your team members
        </p>
      </div>

      {/* Rating Scale Legend */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="py-4">
          <p className={cn("text-sm font-medium mb-3", isDark ? "text-white" : "text-slate-700")}>
            Rating Scale (1-5):
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              { score: 1, label: 'Significant improvement required', color: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
              { score: 2, label: 'Below expectations', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' },
              { score: 3, label: 'Meets expectations', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' },
              { score: 4, label: 'Strong performance', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
              { score: 5, label: 'Exceptional performance', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' },
            ].map(item => (
              <span key={item.score} className={cn("px-3 py-1 rounded-full text-xs font-medium", item.color)}>
                {item.score} - {item.label}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selection Row */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : ""}>Designer</Label>
              <Select value={selectedDesigner} onValueChange={setSelectedDesigner}>
                <SelectTrigger data-testid="score-designer-select" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <SelectValue placeholder="Select designer" />
                </SelectTrigger>
                <SelectContent>
                  {designers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : ""}>Year</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger data-testid="score-year-select" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : ""}>Month</Label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger data-testid="score-month-select" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, index) => (
                    <SelectItem key={index} value={(index + 1).toString()}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : ""}>Week</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((week) => (
                  <button
                    key={week}
                    onClick={() => handleWeekChange(week)}
                    data-testid={`week-${week}-btn`}
                    className={cn(
                      "flex-1 h-10 rounded-lg border-2 font-medium transition-all relative",
                      selectedWeek === week
                        ? "bg-[#331062] border-[#331062] text-white"
                        : isDark
                          ? "border-slate-600 text-slate-300 hover:border-[#331062]"
                          : "border-slate-200 text-slate-600 hover:border-[#331062]"
                    )}
                  >
                    W{week}
                    {weekHasScore(week) && (
                      <CheckCircle className="w-3 h-3 absolute -top-1 -right-1 text-emerald-500 bg-white rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Selected Designer Info */}
          {selectedDesignerData && (
            <div className={cn(
              "mt-4 p-3 rounded-lg flex items-center gap-4",
              isDark ? "bg-slate-700/50" : "bg-slate-50"
            )}>
              <div className="w-10 h-10 rounded-full bg-[#331062] flex items-center justify-center text-white font-bold">
                {selectedDesignerData.name.charAt(0)}
              </div>
              <div>
                <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                  {selectedDesignerData.name}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  {selectedDesignerData.role} • {selectedDesignerData.sub_team || 'No team assigned'}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Score Form */}
      {selectedDesigner && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Core Parameters */}
          <div className="space-y-3">
            <h2 className={cn("text-lg font-semibold flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              <Target className="w-5 h-5 text-[#331062]" />
              Core Performance Parameters
              <span className="text-red-500 text-sm font-normal">(Required)</span>
            </h2>
            
            {PERFORMANCE_PARAMS.map((param) => (
              <ParameterCard
                key={param.key}
                param={param}
                value={formData[param.key]}
                reason={formData[`${param.key}_reason`]}
                onScoreChange={(score) => setFormData({ ...formData, [param.key]: score })}
                onReasonChange={(reason) => setFormData({ ...formData, [`${param.key}_reason`]: reason })}
                isDark={isDark}
                expanded={expandedParams[param.key]}
                onToggle={() => toggleParam(param.key)}
              />
            ))}
          </div>

          {/* Error Control Parameter - Now Required per Policy */}
          <div className="space-y-3">
            <h2 className={cn("text-lg font-semibold flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Error Control / Revision Discipline
              <span className="text-red-500 text-sm font-normal">(Required - 15%)</span>
            </h2>
            
            <ParameterCard
              param={ERROR_CONTROL_PARAM}
              value={formData.error_control}
              reason={formData.error_control_reason}
              onScoreChange={(score) => setFormData({ ...formData, error_control: score })}
              onReasonChange={(reason) => setFormData({ ...formData, error_control_reason: reason })}
              isDark={isDark}
              expanded={expandedParams.error_control}
              onToggle={() => toggleParam('error_control')}
            />
          </div>

          {/* Active Days & Contribution Section */}
          <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardHeader>
              <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")}>
                <Calendar className="w-5 h-5 text-[#331062]" />
                Working Days & Contribution
                <span className={cn("text-sm font-normal", isDark ? "text-slate-400" : "text-slate-500")}>(Required)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Active Working Days */}
                <div className="space-y-3">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    Active Working Days
                    <span className="text-red-500">*</span>
                  </Label>
                  <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                    How many days was the designer actively working this week? (Excludes approved leave)
                  </p>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((days) => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setFormData({ ...formData, active_days: days })}
                        className={cn(
                          "flex-1 h-12 rounded-lg border-2 font-bold text-lg transition-all",
                          formData.active_days === days
                            ? days < 3 
                              ? "bg-amber-500 border-amber-500 text-white"
                              : "bg-[#331062] border-[#331062] text-white"
                            : isDark
                              ? "border-slate-600 text-slate-300 hover:border-slate-500"
                              : "border-slate-200 text-slate-600 hover:border-slate-400"
                        )}
                        data-testid={`active-days-${days}`}
                      >
                        {days}
                      </button>
                    ))}
                  </div>
                  {formData.active_days < 3 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Designer will be marked as "Not Ranked - Low Active Days"
                    </p>
                  )}
                </div>

                {/* Contribution Score */}
                <div className="space-y-3">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    Contribution Score (%)
                    <span className="text-red-500">*</span>
                  </Label>
                  <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                    Output delivered relative to expected work for active days (0-100%)
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.contribution_score}
                      onChange={(e) => setFormData({ ...formData, contribution_score: parseInt(e.target.value) })}
                      className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-700"
                      data-testid="contribution-slider"
                    />
                    <div className={cn(
                      "w-16 h-12 rounded-lg flex items-center justify-center font-bold text-lg border-2",
                      formData.contribution_score >= 90 ? "bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/50 dark:border-emerald-600 dark:text-emerald-300" :
                      formData.contribution_score >= 70 ? "bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/50 dark:border-blue-600 dark:text-blue-300" :
                      formData.contribution_score >= 50 ? "bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900/50 dark:border-amber-600 dark:text-amber-300" :
                      "bg-red-100 border-red-300 text-red-700 dark:bg-red-900/50 dark:border-red-600 dark:text-red-300"
                    )}>
                      {formData.contribution_score}%
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Policy Note */}
              <div className={cn(
                "p-3 rounded-lg text-sm",
                isDark ? "bg-slate-700/50" : "bg-blue-50"
              )}>
                <p className={cn("flex items-start gap-2", isDark ? "text-slate-300" : "text-blue-700")}>
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Policy:</strong> Ranking Score = (Performance Score × 80%) + (Contribution Score × 20%). Minimum 3 active days required for ranking.</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Additional Flags */}
          <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardHeader>
              <CardTitle className={cn("text-base", isDark ? "text-white" : "")}>
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Escalation Section */}
              <div className={cn(
                "p-4 rounded-lg border-2",
                formData.escalation !== 'None' 
                  ? formData.escalation === 'Major' 
                    ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20"
                    : "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20"
                  : isDark ? "border-slate-700 bg-slate-700/30" : "border-slate-200 bg-slate-50"
              )}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? "text-slate-300" : ""}>Escalation Level</Label>
                    <Select
                      value={formData.escalation}
                      onValueChange={(v) => setFormData({ ...formData, escalation: v })}
                    >
                      <SelectTrigger data-testid="escalation-select" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">None</SelectItem>
                        <SelectItem value="Minor">Minor</SelectItem>
                        <SelectItem value="Major">Major</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.escalation !== 'None' && (
                    <div className="space-y-2">
                      <Label className={isDark ? "text-slate-300" : ""}>
                        Escalation Screenshot
                        <span className={cn("text-xs ml-2", isDark ? "text-slate-500" : "text-slate-400")}>(Optional)</span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setFormData({ ...formData, escalation_screenshot: reader.result });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className={cn(
                            "text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium",
                            isDark 
                              ? "file:bg-slate-600 file:text-white text-slate-300" 
                              : "file:bg-[#331062] file:text-white"
                          )}
                          data-testid="escalation-screenshot-input"
                        />
                        {formData.escalation_screenshot && (
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, escalation_screenshot: null })}
                            className="text-red-500 hover:text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {formData.escalation_screenshot && (
                        <div className="mt-2">
                          <img 
                            src={formData.escalation_screenshot} 
                            alt="Escalation screenshot" 
                            className="max-h-32 rounded-lg border"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {formData.escalation !== 'None' && (
                  <div className="mt-4 space-y-2">
                    <Label className={isDark ? "text-slate-300" : ""}>
                      Escalation Reason
                      <span className="text-red-500 ml-1">*</span>
                      <span className={cn("text-xs ml-2", isDark ? "text-slate-500" : "text-slate-400")}>
                        (What happened and why?)
                      </span>
                    </Label>
                    <Textarea
                      value={formData.escalation_reason}
                      onChange={(e) => setFormData({ ...formData, escalation_reason: e.target.value })}
                      placeholder="Describe what happened, why it happened, and any impact on the project or team..."
                      rows={3}
                      className={cn(isDark ? "bg-slate-700 border-slate-600" : "")}
                      data-testid="escalation-reason-input"
                    />
                  </div>
                )}
              </div>

              {/* Positive Feedback Section */}
              <div className={cn(
                "p-4 rounded-lg border-2",
                formData.positive_feedback 
                  ? formData.positive_feedback_type === 'Exceptional'
                    ? "border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20"
                    : "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20"
                  : isDark ? "border-slate-700 bg-slate-700/30" : "border-slate-200 bg-slate-50"
              )}>
                  <div className="flex items-center justify-between mb-3">
                    <Label htmlFor="positive_feedback" className={isDark ? "text-slate-300" : ""}>
                      Positive Feedback Received
                    </Label>
                    <Switch
                      id="positive_feedback"
                      checked={formData.positive_feedback}
                      onCheckedChange={(v) => setFormData({ ...formData, positive_feedback: v })}
                      data-testid="positive-feedback-switch"
                    />
                  </div>

                  {formData.positive_feedback && (
                    <div className="space-y-3">
                      {/* Feedback Type */}
                      <div className="space-y-2">
                        <Label className={cn("text-sm", isDark ? "text-slate-300" : "")}>Feedback Type</Label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, positive_feedback_type: 'Normal' })}
                            className={cn(
                              "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all",
                              formData.positive_feedback_type === 'Normal'
                                ? "bg-blue-500 text-white"
                                : isDark ? "bg-slate-600 text-slate-300" : "bg-slate-200 text-slate-600"
                            )}
                          >
                            Normal
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, positive_feedback_type: 'Exceptional' })}
                            className={cn(
                              "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all",
                              formData.positive_feedback_type === 'Exceptional'
                                ? "bg-emerald-500 text-white"
                                : isDark ? "bg-slate-600 text-slate-300" : "bg-slate-200 text-slate-600"
                            )}
                          >
                            ⭐ Exceptional
                          </button>
                        </div>
                      </div>

                      {/* Feedback Description */}
                      <div className="space-y-2">
                        <Label className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          Feedback Details
                        </Label>
                        <Textarea
                          value={formData.positive_feedback_description}
                          onChange={(e) => setFormData({ ...formData, positive_feedback_description: e.target.value })}
                          placeholder="Describe the positive feedback received..."
                          rows={2}
                          className={cn("text-sm", isDark ? "bg-slate-700 border-slate-600" : "")}
                          data-testid="positive-feedback-description"
                        />
                      </div>

                      {/* Feedback Screenshot */}
                      <div className="space-y-2">
                        <Label className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          Feedback Screenshot
                          <span className={cn("text-xs ml-2", isDark ? "text-slate-500" : "text-slate-400")}>(Optional)</span>
                        </Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setFormData({ ...formData, positive_feedback_screenshot: reader.result });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className={cn(
                              "text-xs file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium",
                              isDark 
                                ? "file:bg-slate-600 file:text-white text-slate-300" 
                                : "file:bg-[#331062] file:text-white"
                            )}
                            data-testid="positive-feedback-screenshot-input"
                          />
                          {formData.positive_feedback_screenshot && (
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, positive_feedback_screenshot: null })}
                              className="text-red-500 hover:text-red-600 text-xs"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        {formData.positive_feedback_screenshot && (
                          <div className="mt-2">
                            <img 
                              src={formData.positive_feedback_screenshot} 
                              alt="Feedback screenshot" 
                              className="max-h-24 rounded-lg border"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
            </CardContent>
          </Card>

          {/* Manager Comment Section */}
          <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardHeader>
              <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")}>
                <TrendingUp className="w-5 h-5 text-[#331062]" />
                Manager Feedback
                <span className={cn("text-sm font-normal", isDark ? "text-slate-400" : "text-slate-500")}>(Recommended)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Strength Observed
                  </Label>
                  <Textarea
                    value={formData.strength_observed}
                    onChange={(e) => setFormData({ ...formData, strength_observed: e.target.value })}
                    placeholder="Highlight one strong aspect of performance..."
                    rows={2}
                    className={cn(isDark ? "bg-slate-700 border-slate-600" : "")}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    Area for Improvement
                  </Label>
                  <Textarea
                    value={formData.area_for_improvement}
                    onChange={(e) => setFormData({ ...formData, area_for_improvement: e.target.value })}
                    placeholder="Mention one specific area where improvement is needed..."
                    rows={2}
                    className={cn(isDark ? "bg-slate-700 border-slate-600" : "")}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Expected Improvement</Label>
                <Textarea
                  value={formData.expected_improvement}
                  onChange={(e) => setFormData({ ...formData, expected_improvement: e.target.value })}
                  placeholder="Provide guidance for the next review cycle..."
                  rows={2}
                  className={cn(isDark ? "bg-slate-700 border-slate-600" : "")}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#331062] hover:bg-[#2A0D52] px-8"
              data-testid="save-score-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Save Score
                </span>
              )}
            </Button>
          </div>
        </form>
      )}

      {!selectedDesigner && (
        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardContent className="py-12 text-center">
            <Calendar className={cn("w-12 h-12 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
            <h3 className={cn("text-lg font-medium", isDark ? "text-white" : "text-slate-800")}>
              Select a Designer
            </h3>
            <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
              Choose a team member to enter their weekly scores
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeeklyScores;
